import { computed, ref } from 'vue'

type PublicPlatformStatus = {
  platform_name: string
  platform_brand_logo_url?: string | null
  platform_logo_url: string | null
  platform_backend_logo_url?: string | null
  platform_loading_logo_url?: string | null
  platform_favicon_url?: string | null
  platform_backend_favicon_url?: string | null
  platform_brand_favicon_url?: string | null
  platform_support_email: string | null
  platform_login_headline?: string | null
  platform_login_subheadline?: string | null
  platform_footer_copyright_text?: string | null
  platform_version?: string | null
  platform_base_url?: string | null
  platform_primary_color?: string | null
  platform_secondary_color?: string | null
  whitelabel_enabled?: boolean
  maintenance_mode: boolean
  maintenance_message: string
  require_email_verification: boolean
  platform_theme_defaults?: Record<string, unknown> | null
}

const defaultPlatformStatus = (): PublicPlatformStatus => ({
  platform_name: 'AppForge',
  platform_brand_logo_url: null,
  platform_logo_url: null,
  platform_backend_logo_url: null,
  platform_loading_logo_url: null,
  platform_favicon_url: null,
  platform_backend_favicon_url: null,
  platform_brand_favicon_url: null,
  platform_support_email: null,
  platform_login_headline: null,
  platform_login_subheadline: null,
  platform_footer_copyright_text: null,
  platform_base_url: null,
  platform_primary_color: null,
  platform_secondary_color: null,
  whitelabel_enabled: false,
  maintenance_mode: false,
  maintenance_message: '',
  require_email_verification: false,
  platform_theme_defaults: null,
})

const readBootstrapPlatformStatus = (): Partial<PublicPlatformStatus> | null => {
  if (typeof window === 'undefined')
    return null

  if (window.__APPFORGE_PLATFORM_STATUS__)
    return window.__APPFORGE_PLATFORM_STATUS__

  const bootstrapScript = document.getElementById('appforge-platform-status')
  const bootstrapMeta = document.querySelector('meta[name="appforge-platform-status"]')
  const rawBootstrap = bootstrapScript?.textContent?.trim()
    || bootstrapMeta?.getAttribute('content')
    || ''
  if (!rawBootstrap)
    return null

  try {
    const parsedBootstrap = JSON.parse(rawBootstrap) as Partial<PublicPlatformStatus> | null

    if (!parsedBootstrap)
      return null

    window.__APPFORGE_PLATFORM_STATUS__ = {
      ...defaultPlatformStatus(),
      ...parsedBootstrap,
    }

    return window.__APPFORGE_PLATFORM_STATUS__
  }
  catch (error) {
    console.error('Error parsing platform branding bootstrap:', error)

    return null
  }
}

const mergePlatformStatus = (status?: Partial<PublicPlatformStatus> | null): PublicPlatformStatus => ({
  ...defaultPlatformStatus(),
  ...status,
})

export const applyFavicon = (url?: string | null) => {
  if (typeof document === 'undefined' || !url)
    return

  document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach(link => { link.href = url })
}

const initialBootstrapPlatformStatus = readBootstrapPlatformStatus()

const platformStatus = ref<PublicPlatformStatus>(mergePlatformStatus(initialBootstrapPlatformStatus))

const hasLoaded = ref(!!initialBootstrapPlatformStatus)
const isLoading = ref(false)

export const usePlatformBranding = () => {
  const ensureBootstrapPlatformStatus = () => {
    const bootstrapPlatformStatus = readBootstrapPlatformStatus()
    if (!bootstrapPlatformStatus)
      return false

    platformStatus.value = {
      ...platformStatus.value,
      ...bootstrapPlatformStatus,
    }
    hasLoaded.value = true

    return true
  }

  ensureBootstrapPlatformStatus()

  const brandName = computed(() => platformStatus.value.platform_name || 'AppForge')
  const brandLogoUrl = computed(() => platformStatus.value.platform_brand_logo_url || platformStatus.value.platform_logo_url || null)
  const logoUrl = computed(() => platformStatus.value.platform_logo_url || null)
  const backendLogoUrl = computed(() => platformStatus.value.platform_backend_logo_url || null)
  const loadingLogoUrl = computed(() => platformStatus.value.platform_loading_logo_url || null)
  const faviconUrl = computed(() => platformStatus.value.platform_favicon_url || null)
  const backendFaviconUrl = computed(() => platformStatus.value.platform_backend_favicon_url || null)
  // Favicon DEDICATO del tenant whitelabel - a differenza di faviconUrl/
  // backendFaviconUrl sopra (entrambi asset della PIATTAFORMA, mai
  // per-tenant nonostante il nome simile).
  const brandFaviconUrl = computed(() => platformStatus.value.platform_brand_favicon_url || null)
  const authHeadline = computed(() => platformStatus.value.platform_login_headline || null)
  const authSubheadline = computed(() => platformStatus.value.platform_login_subheadline || null)
  const footerCopyrightText = computed(() => platformStatus.value.platform_footer_copyright_text || null)
  const primaryColor = computed(() => platformStatus.value.platform_primary_color || null)
  const secondaryColor = computed(() => platformStatus.value.platform_secondary_color || null)
  const isWhitelabel = computed(() => !!platformStatus.value.whitelabel_enabled)
  // Versione di rilascio della piattaforma (config/platform.php), mostrata nel
  // footer del pannello. Il flag hasLoaded serve al footer per non mostrarla
  // prima di sapere se il tenant e' whitelabel (isWhitelabel e' false finche'
  // lo stato non e' caricato).
  const platformVersion = computed(() => platformStatus.value.platform_version || null)
  // Dominio del pannello del tenant (whitelabel se pronto, altrimenti il
  // dominio piattaforma di default) - risolto lato backend da
  // TenantWhitelabelBranding::panelBaseUrl() in base al TENANT autenticato,
  // non al dominio da cui si sta navigando in questo momento: un tenant
  // whitelabel loggato tramite www.appforge.it (dominio custom non ancora
  // impostato/pronto o semplicemente non ancora usato) vede comunque il
  // proprio brand nel pannello - window.location.origin in quel caso
  // mostrerebbe ancora "appforge.it", rivelando la piattaforma sottostante.
  const platformBaseUrl = computed(() => platformStatus.value.platform_base_url || null)
  const showPlatformLogoFallback = computed(() => !isWhitelabel.value)
  const platformThemeDefaults = computed(() => platformStatus.value.platform_theme_defaults || null)

  const loadPlatformBranding = async (force = false) => {
    ensureBootstrapPlatformStatus()

    if (hasLoaded.value && !force)
      return platformStatus.value

    if (isLoading.value)
      return platformStatus.value

    isLoading.value = true

    try {
      const data = await $api<Partial<PublicPlatformStatus>>('/public/platform/status')

      platformStatus.value = mergePlatformStatus({
        ...platformStatus.value,
        ...(data || {}),
      })
      hasLoaded.value = true
    } catch (error) {
      console.error('Error loading platform branding:', error)
    } finally {
      isLoading.value = false
    }

    return platformStatus.value
  }

  return {
    platformStatus,
    brandName,
    brandLogoUrl,
    logoUrl,
    backendLogoUrl,
    loadingLogoUrl,
    faviconUrl,
    backendFaviconUrl,
    brandFaviconUrl,
    authHeadline,
    authSubheadline,
    footerCopyrightText,
    primaryColor,
    secondaryColor,
    isWhitelabel,
    platformVersion,
    hasLoaded,
    platformBaseUrl,
    showPlatformLogoFallback,
    platformThemeDefaults,
    loadPlatformBranding,
  }
}
